import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["aria-label", "onClick", "onMouseenter", "data-tooltip"]
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = ["onClick"]
const _hoisted_4 = ["src"]
const _hoisted_5 = ["src"]
const _hoisted_6 = ["aria-label", "onClick"]
const _hoisted_7 = ["src"]

import { Editor } from '@tiptap/vue-3';
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { Action } from '@/types/study-assistant-interfaces';



export default /*@__PURE__*/_defineComponent({
  __name: 'StudyAssistantDropdown',
  props: {
    editor: {},
    actions: {},
    hideDropdown: { type: Boolean },
    tiptapToken: {},
    editorName: {},
    proUser: { type: Boolean }
  },
  emits: ['updateAudioVideoTime'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

let selectionUpdateHandler: (() => void) | null = null;
const showDropdown = ref(false);

onMounted(() => {
    if (props.editor) {
        selectionUpdateHandler = () => shouldShow();
        props.editor.on('selectionUpdate', selectionUpdateHandler);
        props.editor.on('focus', selectionUpdateHandler);
        props.editor.on('blur', () => {
            // Add delay to prevent dropdown from hiding during clicks
            setTimeout(() => {
                showDropdown.value = false;
            }, 200);
        });
    }
});

const isHidden = ref<number[]>([]);

const filteredActions = computed(() => {
    let filteredByEditor = props.actions;
    
    // Filter actions based on editor type
    if (props.editorName === 'transcript') {
        // Remove "Rewrite" action from transcript editor
        filteredByEditor = props.actions.map(action => {
            if (action.type === 'group' && action.children) {
                return {
                    ...action,
                    children: action.children.filter(child => 
                        !(child.type === 'action' && ['Rewrite', 'Proofread'].includes(child.name))
                    )
                };
            }
            return action;
        });
    } else if (props.editorName === 'note') {
        // Remove "Simplify" action from note editor
        filteredByEditor = props.actions.map(action => {
            if (action.type === 'group' && action.children) {
                return {
                    ...action,
                    children: action.children.filter(child => 
                        !(child.type === 'action' && ['Simplify', 'Define'].includes(child.name))
                    )
                };
            }
            return action;
        });
    }
    
    if (isHidden.value.length > 0) {
        // If isHidden is not empty, filter out items at that index.
        return filteredByEditor.filter(
            (_, index) => {
                return !isHidden.value.includes(index);
            }
        );
    }
    return filteredByEditor;
});

const jumpToBookmark = ref(-1);
let showTimeout: ReturnType<typeof setTimeout> | null = null;

watch(jumpToBookmark, (newValue) => {
    emit('updateAudioVideoTime', newValue);
    jumpToBookmark.value = -1;
});

function shouldShow() {
    // Clear any existing timeout
    if (showTimeout) {
        clearTimeout(showTimeout);
        showTimeout = null;
    }

    if (props.hideDropdown || !props.editor) {
        showDropdown.value = false;
        return;
    }

    const { state } = props.editor;
    const parentNode = state.selection.$anchor.parent;
    const selectedText = state.doc.textBetween(
        state.selection.from,
        state.selection.to,
        '\n'
    );
    
    // Analyze selection to determine bookmark status
    const selectionAnalysis = analyzeSelection(state);
    const { isMixedSelection, isOnlyBookmark, isOnlyNonBookmark } = selectionAnalysis;

    // Define visibility rules for menu items
    const visibilityRules = [
        // AI tools (index 0)
        { 
            index: 0, 
            show: props.tiptapToken && props.proUser 
        },
        // Bookmark tools (index 1)
        { 
            index: 1, 
            show: isOnlyBookmark && !isMixedSelection 
        },
        // Add bookmark (index 3)
        { 
            index: 3, 
            show: props.editorName === 'transcript' ? isOnlyNonBookmark : false 
        },
        // Read aloud (index 5)
        { 
            index: 5, 
            show: props.editorName === 'transcript' && props.proUser 
        }
    ];

    // Apply visibility rules
    visibilityRules.forEach(rule => {
        updateIsHidden(rule.index, rule.show ? 'remove' : 'add');
    });

    // Handle bookmark jump
    if (isOnlyBookmark) {
        jumpToBookmark.value = parseInt(parentNode.attrs.id);
    }

    // Show dropdown if conditions are met
    const shouldShowDropdown = props.editor.isFocused && (isOnlyBookmark || selectedText.trim().length > 0);
    
    if (shouldShowDropdown) {
        clearDropdown();
        showTimeout = setTimeout(() => {
            updateDropdownPosition();
            showDropdown.value = true;
        }, 200);
    } else {
        showDropdown.value = false;
    }
}

function analyzeSelection(state: any) {
    let hasBookmark = false;
    let hasNonBookmark = false;

    state.doc.nodesBetween(state.selection.from, state.selection.to, (node: any) => {
        if (node.type.name === 'paragraph') {
            const childNode = node.firstChild;
            const isBookmark = props.editorName === 'transcript' && 
                node.attrs.id !== null && 
                childNode && 
                childNode.marks.length > 0;

            if (isBookmark) {
                hasBookmark = true;
            } else {
                hasNonBookmark = true;
            }
        }
    });

    return {
        isMixedSelection: hasBookmark && hasNonBookmark,
        isOnlyBookmark: hasBookmark && !hasNonBookmark,
        isOnlyNonBookmark: !hasBookmark && hasNonBookmark
    };
}

function updateIsHidden(index: number, action: string) {
    if (action === 'add') {
        !isHidden.value.includes(index) && isHidden.value.push(index);
    } else if (action === 'remove') {
        const idx = isHidden.value.indexOf(index);
        if (idx > -1) {
            isHidden.value.splice(idx, 1);
        }
    }   
}

const dropdownStyle = ref<{
    position: 'absolute' | 'fixed';
    top: string;
    left: string;
    zIndex: number;
    visibility: 'visible' | 'hidden';
}>({
    position: 'absolute',
    top: '0px',
    left: '0px',
    zIndex: 9999,
    visibility: 'hidden'
});

let lastKnownPosition = {
    top: 0,
    left: 0
};

function updateDropdownPosition() {
    if (!props.editor) return;
    
    const { state, view } = props.editor;
    const { from, to } = state.selection;

    const length = filteredActions.value.length;
    
    try {
        // Use DOM selection for more reliable positioning with collaboration
        const selection = window.getSelection();
        let top = 0;
        let left = 0;

        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            
            // Check if we have a valid selection rectangle
            if (rect && (rect.width > 0 || rect.height > 0)) {
                // For long selections, use the start of the selection
                left = Math.max(
                    Math.min(
                        rect.left + rect.width / 2 - 100,
                        window.innerWidth - 220
                    ),
                    10
                );
                
                top = rect.top - (length * 50);
                if (top < 50) {
                    top = rect.bottom;
                }
            }
        } else {
            // Fallback to TipTap coordinates if DOM selection fails
            const start = view.coordsAtPos(from);
            const end = view.coordsAtPos(to);
            
            left = Math.max((start.left + end.left) / 2 - 100, 10);
            top = start.top - (length * 50);
            if (top < 50) {
                top = end.top;
            }
        }

        const positionChanged = Math.abs(top - lastKnownPosition.top) > 25 || Math.abs(left - lastKnownPosition.left) > 25;
        
        if (positionChanged) {
            lastKnownPosition = { top, left };
            dropdownStyle.value = {
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 9999,
                visibility: 'visible'
            };
        }
    } catch (error) {
        console.warn('Error positioning dropdown:', error);
    }
}

const showChildMenu = ref(-1);
const childMenuActions = ref<Action[]>([]);

function mouseEnterEvent(action: Action, index: number) {
    if (action.type === 'group') {
        showChildMenu.value = index;
        childMenuActions.value = action.children;
    }
}

function mouseLeaveEvent() {
    clearDropdown();
}

function clearDropdown() {
    showChildMenu.value = -1;
    childMenuActions.value = [];
}

onUnmounted(() => {
    if (props.editor && selectionUpdateHandler) {
        props.editor.off('selectionUpdate', selectionUpdateHandler);
        props.editor.off('focus', selectionUpdateHandler);
        props.editor.off('blur', selectionUpdateHandler);
    }
});

return (_ctx: any,_cache: any) => {
  return (showDropdown.value && props.editor)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "bubble-dropdown",
        role: "menu",
        "aria-label": "Study Assistant Dropdown",
        style: _normalizeStyle(dropdownStyle.value),
        onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
        onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredActions.value, (action, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: _normalizeClass({
                'bubble-dropdown__option': true,
                'bubble-dropdown__action': true,
                'bubble-dropdown__disabled': action.isDisabled
            }),
            role: "menuitem",
            "aria-label": action.name,
            tabindex: 0,
            onClick: () => {
                if (action.isDisabled) return;
                action.type === 'action' && action.onclick(props.editor)
            },
            onMouseenter: ($event: any) => (mouseEnterEvent(action, index)),
            onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (mouseLeaveEvent())),
            "data-tooltip": action.isDisabled ? action.disabledReason : ''
          }, [
            (action.type === 'group')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(_toDisplayString(action.name) + " ", 1),
                  _cache[3] || (_cache[3] = _createElementVNode("i", {
                    class: "pi pi-chevron-right",
                    "aria-hidden": "true"
                  }, null, -1)),
                  (showChildMenu.value === index)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "bubble-dropdown bubble-dropdown--child",
                        role: "menu",
                        "aria-label": `${action.name} submenu`
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(childMenuActions.value, (action, index) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: index,
                            class: _normalizeClass({
                            'bubble-dropdown__option': true,
                            'bubble-dropdown__action': action.type === 'action',
                            'bubble-dropdown__heading': action.type === 'heading'
                        }),
                            onClick: () => {
                            action.type === 'action' && action.onclick(props.editor)
                        }
                          }, [
                            (action.type === 'action')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(_toDisplayString(action.name) + " ", 1),
                                  _createElementVNode("img", {
                                    src: action.image,
                                    alt: "icon"
                                  }, null, 8, _hoisted_4)
                                ], 64))
                              : (action.type === 'heading')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createElementVNode("img", {
                                      src: action.image,
                                      alt: "icon"
                                    }, null, 8, _hoisted_5),
                                    _createTextVNode(" " + _toDisplayString(action.name), 1)
                                  ], 64))
                                : (action.type === 'colors')
                                  ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(action.children, (color, index) => {
                                      return (_openBlock(), _createElementBlock("div", {
                                        key: index,
                                        class: "bubble-dropdown__color",
                                        role: "button",
                                        "aria-label": `Apply ${color.name} color`,
                                        tabindex: 0,
                                        style: _normalizeStyle({ backgroundColor: color.name }),
                                        onClick: () => {color.type === 'action' && color.onclick()}
                                      }, null, 12, _hoisted_6))
                                    }), 128))
                                  : _createCommentVNode("", true)
                          ], 10, _hoisted_3))
                        }), 128))
                      ], 8, _hoisted_2))
                    : _createCommentVNode("", true)
                ], 64))
              : (action.type === 'action')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(action.name) + " ", 1),
                    _createElementVNode("img", {
                      src: action.image,
                      alt: "icon"
                    }, null, 8, _hoisted_7)
                  ], 64))
                : _createCommentVNode("", true)
          ], 42, _hoisted_1))
        }), 128))
      ], 36))
    : _createCommentVNode("", true)
}
}

})